import React from 'react';
import * as ReactDOM from 'react-dom';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import {
  PieChart,
  LineChart,
  BarChart,
} from 'echarts/charts';
import {
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LegendScrollComponent,
  DataZoomComponent,
  TimelineComponent,
} from 'echarts/components';

import {
  head, assign, isFunction, cloneDeep,
} from 'lodash';
import PropTypes from 'prop-types';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart as BarChartIcon, ViewHeadline as ViewHeadlineIcon } from '@mui/icons-material';

import { isLandscape, isMobile } from '../../../../common/services/isMobile';
import PaperableHOC from '../PaperableHOC';
import { isSafari } from '../../../../common/services/userAgentService';

export const defaultChartsStyle = { height: '35dvh', width: '100%' };
export const defaultChartsStyleMobile = { height: 300, width: '100%' };

echarts.use(
  [
    PieChart,
    LineChart,
    BarChart,
    TooltipComponent,
    TitleComponent,
    LegendComponent,
    LegendScrollComponent,
    DataZoomComponent,
    TimelineComponent,
  ],
);
// TODO: fix this, component without loading not working
const defaultLoadingOption = {
  text: '',
  color: '#006064',
  textColor: '#000',
  maskColor: 'rgba(255, 255, 255, 0.0)',
  zlevel: 0,
};

export const paperStyleDesktop = {
  height: 'auto',
  padding: 15,
  marginLeft: 20,
  marginRight: 20,
  marginTop: 10,
  textAlign: 'center',
  position: 'relative',
};
export const paperStyleMobile = {
  height: 'auto',
  padding: 5,
  marginTop: 5,
  marginLeft: 5,
  marginRight: 5,
  textAlign: 'center',
  position: 'relative',
};

export const paperStyle = isMobile ? paperStyleMobile : paperStyleDesktop;
export const chartStyle = isMobile && isLandscape ? defaultChartsStyleMobile : defaultChartsStyle;

export const MENU_TOGGLED_EVENT = 'menuToggle'

class DrillDownChart extends React.Component {
  constructor(props) {
    super(props);
    this.handleMenuToggle = this.handleMenuToggle.bind(this);
  }

  handleMenuToggle() {
    Object.values(this.refs).forEach((item) => {
      const instance = item.getEchartsInstance();
      instance.showLoading();
      instance.resize();
      instance.hideLoading();
    });
  }

  componentDidMount() {
    if (!this.props.disableInit) {
      this.props.init();
    }
    if (this.props.clearOnOpen) {
      Object.values(this.refs).forEach((item) => {
        item.getEchartsInstance().clear();
      });
    }
    window.addEventListener(MENU_TOGGLED_EVENT, this.handleMenuToggle)
  }

  componentWillUnmount() {
    if (this.props.clearOnOpen) {
      Object.values(this.refs).forEach((item) => {
        item.getEchartsInstance().clear();
      });
    }
    window.removeEventListener(MENU_TOGGLED_EVENT, this.handleMenuToggle)
  }

  getBaseChartData(filter) {
    const firstChartDataLoad = head(this.props.onGetDataAction);

    if (isFunction(firstChartDataLoad)) {
      this.props.getData(firstChartDataLoad, filter);
    }
  }

  mergeOptions(index, updatedOptions = {}) {
    const options = cloneDeep(this.props.data.options[index]);

    if (options.baseOption) {
      assign(options.baseOption, updatedOptions);
    } else {
      assign(options, updatedOptions);
    }

    return options;
  }

  drillUpdateByIndex(index, data, optionsToSave = {}, filter) {
    const nextChartDataload = this.props.onGetDataAction[index];
    const chartCurrentOptions = cloneDeep(this.props.data.options[index]);

    this.props.updateOptions(index, optionsToSave);
    if (chartCurrentOptions !== null) {
      this.props.updateDataWithoutDispatch(nextChartDataload, chartCurrentOptions, data, filter);
    }
  }

  updateOptionsByIndex(index, updatedOptions = {}) {
    const options = this.mergeOptions(index, updatedOptions);

    this.props.updateOptions(index, options);
  }

  nextDrillDownLoad(index, data, optionsToSave, filter, nextChartOptions) {
    const nextChartDataload = this.props.onGetDataAction[index];
    const currentChartOptions = this.mergeOptions(index - 1, optionsToSave);

    if (!this.props.updateOptionsDisabled) this.props.updateOptions(index - 1, currentChartOptions);

    this.props.getDetailsData(nextChartDataload, nextChartOptions, data, filter).then(() => {
      const lastChartNode = ReactDOM.findDOMNode(this.refs[index]);

      if (!isSafari && lastChartNode) { // TODO will be fixed in I2P2-4034
        lastChartNode.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }

  renderToggle = (viewMode) => {
    const { isGraphAvailable = true, isTableAvailable = true } = this.props;

    return (viewMode !== 'default'
      && (
        <div className="chart-toggle">
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(e, model) => {
              this.props.handleViewModeChange(model);
            }}
          >
            { isGraphAvailable && (
              <ToggleButton
                key={1}
                title="Graph"
                value="graph"
              >
                <BarChartIcon />
              </ToggleButton>
            )}
            {
              isTableAvailable && (
                <ToggleButton
                  key={2}
                  title="List"
                  value="list"
                >
                  <ViewHeadlineIcon />
                </ToggleButton>
              )
            }
          </ToggleButtonGroup>
        </div>
      )
    );
  };

  render() {
    const {
      onEvents = [],
      chartsStyle = [],
      renderers = [],
      loadingOption = defaultLoadingOption,
      noPaper,
      dontShowLoading,
      mergeChartProps,
      data: { options = [], isFetching = false } = {},
      viewMode = 'default',
      listRenderer,
      header,
      noShadow,
    } = this.props;

    return (
      <>
        {
          options.map((option, i) => {
            const renderer = renderers[i];

            if (renderer) {
              return renderer(option, i, this.props);
            }

            return (
              <PaperableHOC
                key={i}
                noPaper={noPaper}
                paperStyle={paperStyle}
                noShadow={noShadow}
              >
                {header && <div className="chart-title">{header}</div>}
                {this.renderToggle(viewMode)}
                {
                  (viewMode === 'graph' || viewMode === 'default')
                  && (
                    <ReactEChartsCore
                      ref={i}
                      loadingOption={loadingOption}
                      showLoading={!dontShowLoading && isFetching}
                      echarts={echarts}
                      notMerge={!mergeChartProps}
                      silent
                      lazyUpdate
                      option={option || {}}
                      style={chartsStyle[i] || chartStyle}
                      onEvents={onEvents[i] || null}
                    />
                  )
                }
                {viewMode === 'list' && listRenderer()}
              </PaperableHOC>
            );
          })
        }
      </>
    );
  }
}

DrillDownChart.propTypes = {
  data: PropTypes.object,
  updateOptionsDisabled: PropTypes.bool,
  clearOnOpen: PropTypes.bool,
  noPaper: PropTypes.bool,
  dontShowLoading: PropTypes.bool,
  mergeChartProps: PropTypes.bool,
  onEvents: PropTypes.array,
  chartsStyle: PropTypes.array,
  loadingOption: PropTypes.object,
  onGetDataAction: PropTypes.array,
  renderers: PropTypes.array,
  init: PropTypes.func,
  getData: PropTypes.func,
  getDetailsData: PropTypes.func,
  updateDataWithoutDispatch: PropTypes.func,
  updateOptions: PropTypes.func,
  viewMode: PropTypes.string,
  handleViewModeChange: PropTypes.func,
  listRenderer: PropTypes.func,
  header: PropTypes.string,
};

DrillDownChart.defaultProps = {
  updateOptionsDisabled: false,
  clearOnOpen: false,
  noPaper: false,
  dontShowLoading: false,
  mergeChartProps: false,
};

export default DrillDownChart;
